import { useMemo } from 'react';
import { Card, Title, Text, Stack, Badge } from '@mantine/core';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import dayjs from 'dayjs';
import type { AggregatedSlot } from '../types/prediction-types';

interface HourlyRevenueChartProps {
  slots: AggregatedSlot[];
  date: string;
}

export default function HourlyRevenueChart({ slots, date }: HourlyRevenueChartProps) {
  const chartData = useMemo(() => {
    // Group slots by hour
    const hourlyData = new Map<number, {
      revenue: number;
      orders: number;
      items: number;
      maxEmployees: number;
      slotCount: number;
    }>();

    slots
      .filter(slot => slot.slot_date === date)
      .forEach(slot => {
        const hour = dayjs(slot.slot_start_time).hour();
        const existing = hourlyData.get(hour) || {
          revenue: 0,
          orders: 0,
          items: 0,
          maxEmployees: 0,
          slotCount: 0,
        };

        hourlyData.set(hour, {
          revenue: existing.revenue + slot.predicted_revenue,
          orders: existing.orders + slot.predicted_orders,
          items: existing.items + slot.predicted_items,
          maxEmployees: Math.max(existing.maxEmployees, slot.employees_needed),
          slotCount: existing.slotCount + 1,
        });
      });

    // Convert to array and format
    return Array.from(hourlyData.entries())
      .map(([hour, data]) => {
        const nextHour = (hour + 1) % 24;
        const timeLabel = `${dayjs().hour(hour).format('h A')}-${dayjs().hour(nextHour).format('h A')}`;
        
        return {
          hour,
          time: timeLabel,
          revenue: parseFloat(data.revenue.toFixed(2)),
          orders: parseFloat(data.orders.toFixed(1)),
          items: parseFloat(data.items.toFixed(1)),
          maxEmployees: data.maxEmployees,
          slotCount: data.slotCount,
        };
      })
      .sort((a, b) => a.hour - b.hour);
  }, [slots, date]);

  if (chartData.length === 0) {
    return null;
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;

    return (
      <Card shadow="md" padding="sm" withBorder>
        <Stack gap="xs">
          <Text size="sm" fw={600}>{data.time}</Text>
          <Text size="xs" c="dimmed">{data.slotCount} time {data.slotCount === 1 ? 'slot' : 'slots'} in this hour</Text>
          <Badge size="sm" color="blue">Revenue: ${data.revenue}</Badge>
          <Badge size="sm" color="grape">{data.orders} orders</Badge>
          <Badge size="sm" color="violet">{data.items} items</Badge>
          <Badge 
            size="sm" 
            color={data.maxEmployees >= 3 ? 'red' : data.maxEmployees >= 2 ? 'orange' : 'blue'}
          >
            Peak: {data.maxEmployees} staff
          </Badge>
        </Stack>
      </Card>
    );
  };

  // Color bars by max employees needed in that hour
  const getBarColor = (employees: number) => {
    if (employees >= 3) return '#fa5252'; // red
    if (employees >= 2) return '#fd7e14'; // orange
    return '#228be6'; // blue
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <div>
          <Title order={4}>Hourly Revenue Summary</Title>
          <Text size="sm" c="dimmed">
            {dayjs(date).format('dddd, MMMM D, YYYY')}
          </Text>
        </div>

        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
            
            <XAxis 
              dataKey="time" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            
            <YAxis 
              label={{ value: 'Revenue ($)', angle: -90, position: 'insideLeft', style: { fontSize: 14 } }}
              tick={{ fontSize: 12 }}
            />
            
            <Tooltip content={<CustomTooltip />} />
            
            <Legend 
              wrapperStyle={{ paddingTop: '10px' }}
              iconType="rect"
            />
            
            <Bar 
              dataKey="revenue" 
              name="Revenue ($)"
              radius={[4, 4, 0, 0]}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(entry.maxEmployees)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        <Stack gap="xs">
          <Text size="sm" fw={600}>Color Legend (by peak staff need):</Text>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: 16, height: 16, borderRadius: 2, backgroundColor: '#228be6' }} />
              <Text size="sm">1 employee</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: 16, height: 16, borderRadius: 2, backgroundColor: '#fd7e14' }} />
              <Text size="sm">2 employees</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: 16, height: 16, borderRadius: 2, backgroundColor: '#fa5252' }} />
              <Text size="sm">3+ employees</Text>
            </div>
          </div>
        </Stack>
      </Stack>
    </Card>
  );
}
